import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { compact, difference, pullAll, union } from 'lodash/fp';
import { useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import { Controller, useFormContext } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect from 'dashboard/components/ReactSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { StyledIconDrag } from 'dashboard/lib/dnd/StyledIconDrag';
import { reorder } from 'dashboard/lib/dnd/utils';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { EditCaseAssignmentPolicyFormData } from 'dashboard/views/DashboardCaseAssignmentPolicyShowView/FormEditCaseAssignmentPolicy';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import { HelperTooltip } from 'design-system/components/Tooltip';
import useToggle from 'lib/hooks/useToggle';

type CaseSource = CaseTemplateBasicsOption | CaseQueue;

const SourceTypeContainer = styled.div`
  max-width: 270px;
`;

const CheckboxTableTH = styled(TableTH)`
  // checkboxes are 16px wide
  width: 16px;
`;

const DndSection = styled('div')`
  overflow: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const TooltipText = styled(Paragraph)`
  max-width: 300px;
`;

const StickyModalHeader = styled(ModalHeader)`
  top: 0;
  position: sticky;
  background: ${({ theme }) => theme.color.background.primary};
`;

const StyledModalContainer = styled(ModalContainer)`
  overflow: hidden;
`;

interface SourceCardProps {
  caseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  caseQueues: CaseQueue[];
}

const SourceCard = ({ caseTemplateBasicsOptions, caseQueues }: SourceCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<EditCaseAssignmentPolicyFormData>();

  const selectedSourceType: 'case_template' | 'case_queue' = watch('caseSourceType');
  const selectedSourceLabel = selectedSourceType === 'case_template' ? 'Case Template' : 'Queue';
  const selectedSourceIds: string[] = watch('caseSourceIds');

  const sourceItems: CaseSource[] =
    selectedSourceType === 'case_template' ? caseTemplateBasicsOptions : caseQueues;

  const selectedSourceItems = compact(
    selectedSourceIds.map((id) => sourceItems.find((item: CaseSource) => item.id === id)),
  );

  const [editSourcesModal, { enable: showEditSourcesModal, disable: hideEditSourcesModal }] =
    useToggle(false);

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const reorderedSourceIds = reorder(selectedSourceIds, source.index, destination.index);
    setValue('caseSourceIds', reorderedSourceIds, { shouldDirty: true });
  };

  const handleRemoveSourceItem = (id: string) => {
    const updatedSourceIds = selectedSourceIds.filter((sourceId) => sourceId !== id);
    setValue('caseSourceIds', updatedSourceIds, { shouldDirty: true });
  };

  const shownDndItemProps: AttributeDndProps[] = selectedSourceItems.map((sourceItem, index) => {
    return {
      hasHighestPriority: index === 0 && selectedSourceItems.length > 1,
      hasLowestPriority: index === selectedSourceItems.length - 1 && selectedSourceItems.length > 1,
      selectedSourceType,
      sourceItem,
      index,
      shouldDisableDrag: false,
      handleRemove: () => handleRemoveSourceItem(sourceItem.id),
    } as AttributeDndProps;
  });

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title
            actions={
              <Button
                id='form-edit-case-assignment-policy__edit-case-source'
                variant='basic'
                onClick={showEditSourcesModal}
                disabled={selectedSourceIds.length === sourceItems.length}
              >
                Add
              </Button>
            }
          >
            Source
            <HelperTooltip hideArrow side='right'>
              <TooltipText color='primary' size='sm'>
                Cases are assigned in priority order, from the highest priority case template or
                queue to the lowest. Within a case template or queue, assignment follows the case
                sort order.
              </TooltipText>
            </HelperTooltip>
          </Card.Title>

          <div>
            <Label
              htmlFor='form-edit-case-assignment-policy__case-source-type'
              id='form-edit-case-assignment-policy__case-source-type--label'
              size='md'
              weight='bold'
            >
              Type
            </Label>
            <Spacer height={1} />
            <SourceTypeContainer>
              <Controller
                control={control}
                name='caseSourceType'
                rules={{
                  required: 'Please select a source type',
                }}
                render={({ field: { onChange, value } }) => (
                  <ReactSelect
                    id='form-edit-case-assignment-policy__case-source-type'
                    aria-labelledby='form-edit-case-assignment-policy__case-source-type--label'
                    options={[
                      { label: 'Case Template', value: 'case_template' },
                      { label: 'Queue', value: 'case_queue' },
                    ]}
                    value={value}
                    onChange={(sourceType: 'case_template' | 'case_queue') => {
                      setValue('caseSourceIds', [], { shouldDirty: true });
                      onChange(sourceType);
                    }}
                    portal
                  />
                )}
              />
            </SourceTypeContainer>
            {errors.caseSourceType && <FormError>{errors.caseSourceType.message}</FormError>}
            <Spacer height={1} />
          </div>
          <DndSection>
            <TableLayout
              showHeader={true}
              isEmpty={selectedSourceIds.length === 0}
              nullState={
                <TableNullState>
                  <p>No {selectedSourceLabel}s have been selected.</p>
                  <Spacer height={1} />
                  {sourceItems.length > 0 && (
                    <Button
                      id='form-edit-case-assignment-policy__null-state-edit-case-source'
                      variant='basic'
                      onClick={showEditSourcesModal}
                      width='auto'
                    >
                      Add {selectedSourceLabel}
                    </Button>
                  )}
                </TableNullState>
              }
              headerTR={
                <>
                  <TableTH>Name</TableTH>
                </>
              }
            >
              {selectedSourceItems.length > 0 && (
                <div>
                  {/* Issue with react-beautiful-dnd and transformed parent elements: https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md
            We have to use the renderClone API to get the dragging working correctly. This has to do with one of the parent elements being a Popover. */}
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable
                      droppableId='form-edit-case-assignment-policy__case-sources'
                      renderClone={(provided, _snapshot, rubric) => {
                        return (
                          <div
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                          >
                            <AttributeDnd {...shownDndItemProps[rubric.source.index]} />
                          </div>
                        );
                      }}
                    >
                      {(provided) => {
                        return (
                          <div ref={provided.innerRef} {...provided.droppableProps}>
                            {shownDndItemProps.map((props, idx) => {
                              return (
                                <Draggable
                                  key={`${props.sourceItem.id}`}
                                  draggableId={props.sourceItem.id}
                                  index={idx}
                                >
                                  {(provided) => {
                                    return (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <AttributeDnd {...props} />
                                      </div>
                                    );
                                  }}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        );
                      }}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}
            </TableLayout>
          </DndSection>
        </Card.Body>
      </Card>
      <ModalWrapper
        isOpen={editSourcesModal}
        handleCloseModal={hideEditSourcesModal}
        styleOverride={{
          content: {
            // Height must be specified on component with overflow auto for child sticky components to work
            height: '100%',
            // Limits the height of the modal so we can have scroll and sticky behaviour within the modal
            maxHeight: '90%',
          },
        }}
      >
        <StyledModalContainer>
          <StickyModalHeader onClose={hideEditSourcesModal}>Add sources</StickyModalHeader>
          <AddSourceTable
            sourceItems={sourceItems.filter(
              (sourceItem) => !selectedSourceIds.includes(sourceItem.id),
            )}
            selectedSourceType={selectedSourceType}
            onChange={(ids) => {
              setValue('caseSourceIds', [...selectedSourceIds, ...ids], { shouldDirty: true });
            }}
            handleClose={hideEditSourcesModal}
          />
        </StyledModalContainer>
      </ModalWrapper>
    </>
  );
};

const DndItemWrapper = styled('div')`
  background: white;
  width: 100%;
  padding: ${Spacing[2]} ${Spacing[2]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
`;

const SuffixContainer = styled('div')`
  padding-left: ${Spacing[2]};
  margin-left: auto;
  align-self: center;
`;

const PriorityLabel = styled(Paragraph)`
  margin-left: ${Spacing[2]};
`;

interface AttributeDndProps {
  hasHighestPriority: boolean;
  hasLowestPriority: boolean;
  index: number;
  selectedSourceType: 'case_queue' | 'case_template';
  sourceItem: CaseQueue | CaseTemplateBasicsOption;
  shouldDisableDrag: boolean;
  handleRemove: () => void;
}

const AttributeDnd = ({
  hasHighestPriority,
  hasLowestPriority,
  sourceItem,
  selectedSourceType,
  index,
  shouldDisableDrag,
  handleRemove,
}: AttributeDndProps) => {
  const name =
    selectedSourceType === 'case_template'
      ? `${(sourceItem as CaseTemplateBasicsOption).name} (${
          (sourceItem as CaseTemplateBasicsOption)['case-prefix']
        })`
      : (sourceItem as CaseQueue).attributes.name;

  return (
    <DndItemWrapper
      id={`form-edit-case-assignment-policy__case-source-ids--${sourceItem.id}`}
      key={`${sourceItem.id}-${index}`}
    >
      <StyledIconDrag $active $shouldHide={shouldDisableDrag} />
      <Paragraph color='primary'>{name}</Paragraph>
      {hasHighestPriority && (
        <PriorityLabel color='tertiary' size='sm'>
          Highest priority
        </PriorityLabel>
      )}
      {hasLowestPriority && (
        <PriorityLabel color='tertiary' size='sm'>
          Lowest priority
        </PriorityLabel>
      )}
      <SuffixContainer>
        <Button
          id={`form-edit-case-assignment-policy__case-source-ids--remove-${sourceItem.id}`}
          width='icon'
          variant='inverse'
          onClick={handleRemove}
        >
          <TrashIcon />
        </Button>
      </SuffixContainer>
    </DndItemWrapper>
  );
};

const AddSourceTableWrapper = styled('div')`
  overflow: auto;
`;

const TableWrapper = styled('div')`
  padding-top: ${Spacing[3]};
  padding-left: ${Spacing[7]};
  padding-right: ${Spacing[7]};
`;

const SearchWrapper = styled('div')`
  top: 0;
  position: sticky;
  background: ${({ theme }) => theme.color.background.primary};

  padding-left: ${Spacing[7]};
  padding-right: ${Spacing[7]};

  z-index: 1;
`;

const StyledButtonGroup = styled(HStack)`
  bottom: 0;
  position: sticky;
  background: ${({ theme }) => theme.color.background.primary};

  padding: ${Spacing[3]} ${Spacing[7]} ${Spacing[7]} ${Spacing[7]};
`;

interface AddSourceTableProps {
  sourceItems: (CaseTemplateBasicsOption | CaseQueue)[];
  selectedSourceType: 'case_template' | 'case_queue';
  onChange: (ids: string[]) => void;
  handleClose: () => void;
}
const AddSourceTable = ({
  sourceItems,
  selectedSourceType,
  onChange,
  handleClose,
}: AddSourceTableProps) => {
  const [selectedSourceIds, updateSelectedSourceIds] = useState<string[]>([]);
  const [lastSelectedSourceIndex, setLastSelectedSourceIndex] = useState<number | undefined>(
    undefined,
  );
  const [searchTerm, setSearchTerm] = useState('');

  const fuse = useMemo(() => {
    const keyName = selectedSourceType === 'case_template' ? 'name' : 'attributes.name';
    const fuseOptions = {
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [{ name: keyName, weight: 1.0 }],
    };

    return new Fuse(sourceItems, fuseOptions);
  }, [sourceItems]);

  const filteredSourceItems =
    searchTerm === '' ? sourceItems : fuse.search(searchTerm).map(({ item }) => item);

  const sourceRows = filteredSourceItems.map((sourceItem, key) => {
    const checked = selectedSourceIds.some((sourceId) => sourceId === sourceItem.id);

    const name =
      selectedSourceType === 'case_template'
        ? (sourceItem as CaseTemplateBasicsOption).name
        : (sourceItem as CaseQueue).attributes.name;

    return (
      <TableTR
        id={`form-edit-case-assignment-policy__case-source-ids--${sourceItem.id}`}
        key={`form-edit-case-assignment-policy__case-source-ids--${sourceItem.id}`}
        onClick={(e) => {
          handleSelectSourceId(key, e.shiftKey);
        }}
      >
        <TableTD id={`checkbox--${sourceItem.id}`}>
          <Checkbox
            name={`cases-table__checkbox--${sourceItem.id}`}
            data-test={`cases-table__checkbox--${sourceItem.id}`}
            checked={checked}
            onChange={() => {
              // TODO: Make checkbox keyboard interactable
            }}
          />
        </TableTD>
        <TableTD>{name}</TableTD>
      </TableTR>
    );
  });

  const handleSelectSourceId = (sourceIdIndex: number, shiftKey: boolean) => {
    const selectedSourceId = filteredSourceItems[sourceIdIndex].id;
    const isUnselectMode = selectedSourceIds.some((sourceId) => sourceId === selectedSourceId);

    let nextSourceIds: string[];
    if (lastSelectedSourceIndex != null && shiftKey) {
      const startIndex = Math.min(sourceIdIndex, lastSelectedSourceIndex);
      const endIndex = Math.max(sourceIdIndex, lastSelectedSourceIndex);

      nextSourceIds = filteredSourceItems
        .slice(startIndex, endIndex + 1)
        .map((sourceItem) => sourceItem.id);

      nextSourceIds = isUnselectMode
        ? pullAll(nextSourceIds, selectedSourceIds)
        : union(nextSourceIds, selectedSourceIds);
    } else {
      nextSourceIds = isUnselectMode
        ? pullAll([selectedSourceId], selectedSourceIds)
        : union([selectedSourceId], selectedSourceIds);
    }

    updateSelectedSourceIds(nextSourceIds);
    setLastSelectedSourceIndex(sourceIdIndex);
  };

  return (
    <AddSourceTableWrapper>
      <SearchWrapper>
        <SearchInput
          id='add-source-table__search-input'
          title=''
          handleInput={setSearchTerm}
          value={searchTerm}
          placeholder='Search'
          size='md'
        />
      </SearchWrapper>
      <TableWrapper>
        <Spacer height={1} />
        <TableLayout
          showHeader={true}
          nullState={null}
          headerTR={
            <>
              <CheckboxTableTH isSticky>
                <Checkbox
                  name='cases-table__checkbox--select-all'
                  data-test='cases-table__checkbox--select-all'
                  checked={selectedSourceIds.length === sourceItems.length}
                  indeterminate={
                    selectedSourceIds.length > 0 && selectedSourceIds.length < sourceItems.length
                  }
                  onChange={() => {
                    const filteredSourceIds = filteredSourceItems.map((item) => item.id);
                    // Checks if all of the currently filtered items are selected
                    if (difference(filteredSourceIds, selectedSourceIds).length === 0) {
                      updateSelectedSourceIds(difference(selectedSourceIds, filteredSourceIds));
                    } else {
                      updateSelectedSourceIds(union(filteredSourceIds, selectedSourceIds));
                    }
                  }}
                />
              </CheckboxTableTH>
              <TableTH isSticky>Name</TableTH>
            </>
          }
        >
          {sourceRows}
        </TableLayout>
      </TableWrapper>
      <Spacer height={2} />
      <StyledButtonGroup equal={true}>
        <Button
          id='form-edit-case-assignment-policy__case-source-ids__cancel_button'
          variant='basic'
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          id='form-edit-case-assignment-policy__case-source-update_button'
          variant='primary'
          type='submit'
          onClick={() => {
            onChange(selectedSourceIds);
            handleClose();
          }}
        >
          Update
        </Button>
      </StyledButtonGroup>
    </AddSourceTableWrapper>
  );
};

export default SourceCard;
